/**
 * MA-THEME-OVERRIDE-151: FunnelLivePreview Renderer Parity
 * 
 * CRITICAL: This component must remain in parity with the standalone version at:
 * /components/funnels/builder/FunnelLivePreview.jsx
 * 
 * THIS (PPB) version is the canonical visual source of truth.
 * All visual updates must originate from this implementation.
 * 
 * TODO (Backlog): Consolidate into single shared renderer component.
 */

/**
 * MA-UX-402 Phase C: FunnelLivePreview
 *
 * Read-only preview of actual funnel nodes.
 * Colors from builder_data.theme applied to the funnel card.
 * Response area uses contrasting shade of the themed container.
 * Only Input nodes show input fields — Response nodes do NOT.
 */

import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Shield, ChevronLeft, ChevronRight, Calendar, ArrowUp, Clock,
} from "lucide-react";
import { resolveHeaderIcon, getHeaderIconType } from "@/lib/headerIcons";
import { resolveThemeTokens } from "@/lib/themeAdapters";  // MA-216: Shared canonical token resolver
import { QuickBookingWidget } from "@/components/booking";  // MA-FUNNEL-ARCH-073: Unified booking component

// MA-FUNNEL-ARCH-073: Mock Slots for Preview Mode
// CRITICAL: Must match API response shape exactly from /api/booking/slots/{calendarId}
// API returns: { today_slots: [...], tomorrow_slots: [...] }
const PPB_MOCK_SLOTS = {
  today_slots: [
    { slot_id: 'mock_today_1', display_time: '10:30 AM', start_time: '2024-01-01T10:30:00Z', duration_minutes: 15 },
    { slot_id: 'mock_today_2', display_time: '2:00 PM', start_time: '2024-01-01T14:00:00Z', duration_minutes: 15 },
    { slot_id: 'mock_today_3', display_time: '4:30 PM', start_time: '2024-01-01T16:30:00Z', duration_minutes: 15 },
  ],
  tomorrow_slots: [
    { slot_id: 'mock_tomorrow_1', display_time: '9:00 AM', start_time: '2024-01-02T09:00:00Z', duration_minutes: 15 },
    { slot_id: 'mock_tomorrow_2', display_time: '11:00 AM', start_time: '2024-01-02T11:00:00Z', duration_minutes: 15 },
  ],
};

function getContrastingShade(hexColor) {
  try {
    const hex = hexColor.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    const factor = luminance < 0.5 ? 1.35 : 0.88;
    const clamp = (v) => Math.min(255, Math.max(0, Math.round(v * factor)));
    return `#${clamp(r).toString(16).padStart(2, "0")}${clamp(g).toString(16).padStart(2, "0")}${clamp(b).toString(16).padStart(2, "0")}`;
  } catch {
    return "#F0F0F0";
  }
}

export function FunnelLivePreview({
  nodes = [],
  appearance = {},
  header = {},
  activeStepIndex = 0,
  onStepClick,
  theme = {},
  lpTokens = null,
  funnelConfig = null,
}) {
  // MA-287: Diagnostic logging for theme source investigation
  React.useEffect(() => {
    console.log('[MA-287][PPB SOURCE] lpTokens:', lpTokens);
    console.log('[MA-287][PPB SOURCE] appearance:', appearance);
    console.log('[MA-287][PPB SOURCE] theme prop:', theme);
    console.log('[MA-287][PPB SOURCE] funnelConfig:', funnelConfig);
  }, [lpTokens, appearance, theme, funnelConfig]);
  
  // MA-216: Use shared canonical token resolver (SINGLE SOURCE OF TRUTH)
  // All UI element colors derive from resolved map "c"
  const c = resolveThemeTokens(lpTokens, appearance);
  
  // [MA-FUNNEL-ARCH-084] AUDIT: Log resolved tokens for comparison
  console.log('[MA-FUNNEL-ARCH-084] PPB Token Resolution - Full "c" object:', {
    'c.containerColor': c.containerColor,
    'c.threadColor': c.threadColor,
    'c.botBubbleBg': c.botBubbleBg,
    'c.cardBg': c.cardBg,
    'c.containerBg': c.containerBg,
    'c.surface_elevated': c.surface_elevated,
    'c.surface_base': c.surface_base,
    'c.textPrimary': c.textPrimary,
    'c.textSecondary': c.textSecondary,
    'c.textMuted': c.textMuted,
    'c.accent': c.accent,
    'c.userColor': c.userColor,
    'c.surfaceMode': c.surfaceMode,
    'appearance.surfaceMode': appearance?.surfaceMode,
    'lpTokens.surfaceMode': lpTokens?.surfaceMode,
  });
  
  const userColor = c.userColor;
  const containerColor = c.containerColor;
  const threadColor = c.threadColor;
  const botBubbleBg = c.botBubbleBg;
  const botBubbleText = c.botBubbleText;
  const userTextColor = c.userTextColor;
  const rawBorderColor = c.borderColor;  // Original border color before calibration
  const textMuted = c.textMuted;
  const headerText = c.headerText;

  // MA-305 PPB Calibration: Local light theme token overrides for visual hierarchy (PPB Funnel Step ONLY)
  const isLightMode = appearance?.surfaceMode === 'light' || lpTokens?.surfaceMode === 'light';
  const localThreadColor = isLightMode ? '#f8fafc' : threadColor;
  const localHeaderBg = isLightMode ? '#f1f5f9' : (appearance?.headerBackgroundColor || containerColor);
  const localResponseBg = isLightMode ? '#f1f5f9' : (appearance?.responseAreaColor || containerColor);
  const localBotBubbleBg = isLightMode ? '#e5e7eb' : (appearance?.botColor || appearance?.containerColor || botBubbleBg);
  const finalBorderColor = isLightMode ? '#e2e8f0' : rawBorderColor;  // Calibrated border

  // Use local calibrated values
  const responseAreaColor = localResponseBg;
  const headerBackgroundColor = localHeaderBg;
  const botColor = localBotBubbleBg;
  const canvasBg = "transparent";

  // MA-305 Calibration: Validation logging (shows calibrated values)
  React.useEffect(() => {
    console.log("PPB COLORS (Calibrated):", {
      mode: isLightMode ? 'light' : 'dark',
      thread: localThreadColor,
      header: localHeaderBg,
      response: localResponseBg,
      bubble: localBotBubbleBg,
      border: finalBorderColor
    });
  }, [isLightMode, localThreadColor, localHeaderBg, localResponseBg, localBotBubbleBg, finalBorderColor]);

  // MA-277: Theme debug instrumentation (dev/preview only)
  // MA-277C: Fixed scope - funnel object not available in this component
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const debugTheme = urlParams.get('debugTheme') === '1';
    
    if (debugTheme) {
      console.log('[FUNNEL_THEME_DEBUG]', {
        surface: 'PPB_Funnel_Step',
        funnelId: 'preview_only',
        stylingMode: 'themeAdapters',
        surfaceMode: appearance?.surfaceMode || lpTokens?.surfaceMode || 'unknown',
        
        appearance: {
          containerColor: appearance?.containerColor,
          threadColor: appearance?.threadColor,
          headerBackgroundColor: appearance?.headerBackgroundColor,
          userColor: appearance?.userColor,
          botTextColor: appearance?.botTextColor,
          userTextColor: appearance?.userTextColor,
          iconBackgroundColor: appearance?.iconBackgroundColor,
        },
        
        lpTokens: lpTokens ? {
          bgPage: lpTokens.bgPage,
          cardBg: lpTokens.cardBg,
          accent: lpTokens.accent,
          textPrimary: lpTokens.textPrimary,
          textOnAccent: lpTokens.textOnAccent,
          borderSubtle: lpTokens.borderSubtle,
        } : null,
        
        resolved: {
          threadColor,
          botBubbleBg,
          userColor,
          headerBg: containerColor,
          finalBorderColor,
        },
        
        sources: {
          threadColor: appearance?.threadColor ? 'appearance.threadColor' : 'lpTokens.bgPage',
          botBubbleBg: appearance?.containerColor ? 'appearance.containerColor' : 'lpTokens.cardBg',
          userColor: appearance?.userColor ? 'appearance.userColor' : (appearance?.accent ? 'appearance.accent' : 'lpTokens.accent'),
          headerBg: appearance?.containerColor ? 'appearance.containerColor' : 'lpTokens.cardBg',
          finalBorderColor: appearance?.finalBorderColor ? 'appearance.finalBorderColor' : 'default_rgba',
        },
        
        visualModel: {
          separation: 'fill-contrast (borders removed in MA-275)',
          threadBgValue: threadColor,
          botBubbleValue: botBubbleBg,
          contrast: threadColor === botBubbleBg ? 'COLLISION' : 'distinct',
        }
      });
    }
  }, [appearance, lpTokens, threadColor, botBubbleBg, userColor, containerColor, finalBorderColor]);

  // Scale the card proportionally based on available container width
  const containerRef = useRef(null);
  const observerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const BASE_CARD_WIDTH = 280; // Fixed design width

  const measureAndScale = useCallback((node) => {
    if (!node) return;
    const containerWidth = node.offsetWidth;
    const targetWidth = containerWidth * 0.65;
    const newScale = Math.max(0.8, Math.min(1.6, targetWidth / BASE_CARD_WIDTH));
    setScale(newScale);
  }, []);

  // Callback ref — fires reliably when the DOM node mounts/unmounts
  const setContainerRef = useCallback((node) => {
    // Cleanup old observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    containerRef.current = node;
    if (node) {
      measureAndScale(node);
      const ro = new ResizeObserver(() => measureAndScale(node));
      ro.observe(node);
      observerRef.current = ro;
    }
  }, [measureAndScale]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  if (!nodes || nodes.length === 0) return null;

  const node = nodes[activeStepIndex] || nodes[0];
  const content = node.content || {};
  const nodeType = node.node_type;
  
  // MA-234: STRICT CANONICAL READ (no fallback chains)
  const headerTitle = header?.title ?? "Assessment";
  
  // MA-PPB-CONSENT-HEADER-078: Use shared icon resolver
  const HeaderIconComponent = resolveHeaderIcon(header);
  const headerIconType = getHeaderIconType(header);

  // MA-PPB-CONSENT-HEADER-078: Debug instrumentation
  console.log('[FunnelLivePreview] HEADER DEBUG:', {
    'header': header,
    'header.title': header?.title,
    'header.icon_type': header?.icon_type,
    'header.selectedIcon': header?.selectedIcon,
    'header.icon': header?.icon,
    'appearance': appearance,
    'appearance.title': appearance?.title,
    'appearance.selectedIcon': appearance?.selectedIcon,
    headerTitle_resolved: headerTitle,
    headerIconType_resolved: headerIconType,
    HeaderIconComponent_resolved: HeaderIconComponent?.name || 'unknown',
  });

  return (
    <div className="flex flex-col h-full" data-testid="funnel-live-preview">
      <div
        ref={setContainerRef}
        className="flex-1 flex flex-col items-center justify-center rounded-xl overflow-hidden relative"
        style={{ background: canvasBg }}
      >
        {/* Step dots */}
        <div className="absolute top-3 left-0 right-0 flex justify-center gap-1.5 z-10">
          {nodes.map((_, i) => (
            <button
              key={i}
              onClick={() => onStepClick?.(i)}
              className="transition-all duration-200"
              style={{
                width: i === activeStepIndex ? "16px" : "5px",
                height: "5px",
                borderRadius: "3px",
                background: i === activeStepIndex ? userColor : `${textMuted}40`,
                cursor: "pointer",
                border: "none",
              }}
              data-testid={`preview-dot-${i}`}
            />
          ))}
        </div>

        {/* Funnel card — fixed design size, scaled proportionally */}
        <div
          className="origin-center"
          style={{
            width: `${BASE_CARD_WIDTH}px`,
            transform: `scale(${scale})`,
            transition: "transform 0.15s ease-out",
          }}
        >
          <motion.div
            key={activeStepIndex}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
            className="rounded-2xl overflow-hidden"
            style={{
              background: localThreadColor,
              border: `1px solid ${finalBorderColor}`,
              boxShadow: `0 4px 20px rgba(0,0,0,0.3)`,
            }}
          >
            {/* Header — hidden for booking step (matches runtime behavior) */}
            {nodeType !== "booking" && (
            <div
              className="flex items-center gap-2 px-3 py-2"
              style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.06)', background: headerBackgroundColor }}
              data-testid="funnel-preview-header"
            >
              <div
                className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: userColor }}
              >
                {/* MA-PPB-CONSENT-HEADER-078: Use resolved icon component (not hardcoded Shield) */}
                <HeaderIconComponent className="w-3 h-3" style={{ color: userTextColor }} />
              </div>
              <p className="text-[10px] font-semibold" style={{ color: headerText }}>
                {headerTitle}
              </p>
            </div>
            )}

            {/* Message thread area — skip consent and booking (they have their own cards) */}
            {content.message && nodeType !== "consent" && nodeType !== "booking" && (
              <div className="px-3 py-3" style={{ background: threadColor }}>
                <div className="max-w-[88%]">
                  <div
                    className="px-2.5 py-1.5"
                    style={{
                      background: botBubbleBg,
                      color: botBubbleText,
                      borderRadius: "14px 14px 14px 3px",
                    }}
                  >
                    <p className="text-[9px] leading-relaxed" style={{ fontWeight: 500 }}>
                      {nodeType !== "consent" ? (content.message || "Schedule Your Appointment") : content.message || ""}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Response buttons — question/response nodes ONLY */}
            {(nodeType === "question" || nodeType === "response") && content.responses?.length > 0 && (
              <div
                className="px-3 py-2.5"
                style={{ background: responseAreaColor, borderTop: '1px solid rgba(0, 0, 0, 0.06)' }}
              >
                <div className="flex flex-col items-end gap-1">
                  {content.responses.map((resp, i) => (
                    <div
                      key={resp.id || i}
                      className="px-3 py-1.5 text-[9px] font-medium max-w-[85%] text-center"
                      style={{
                        background: userColor,
                        color: userTextColor,
                        borderRadius: "14px 14px 3px 14px",
                      }}
                    >
                      {resp.label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Input field — Standard OR Phone + Consent (MA-162A) */}
            {nodeType === "input" && (
              <>
                {(() => {
                  const isPhoneConsent = content.input_type === "phone" && content.requires_consent === true;
                  const MA_DEBUG_INPUT = {
                    node_id: node.id,
                    node_type: nodeType,
                    input_type: content.input_type,
                    requires_consent: content.requires_consent,
                    has_consent_text: !!content.consent_text,
                    renderer_branch: isPhoneConsent ? "input_phone_consent_inline" : "input_standard",
                    architecture: "MA-162A",
                    timestamp: new Date().toISOString()
                  };
                  console.log('═══════════════════════════════════════════');
                  console.log('MA_DEBUG_INPUT (PPB):');
                  console.log(JSON.stringify(MA_DEBUG_INPUT, null, 2));
                  console.log('═══════════════════════════════════════════');
                  return null;
                })()}

                {/* MA-167: Check if this is Phone + Consent inline mode */}
                {content.input_type === "phone" && content.requires_consent === true ? (
                  /* MA-167: PHONE + CONSENT MODE - Conversation Thread Pattern */
                  <div className="px-3 py-2.5" style={{ background: responseAreaColor, borderTop: '1px solid rgba(0, 0, 0, 0.06)' }}>
                    {/* Phone Input Field - MA-FUNNEL-UI-072: Removed bottom margin to eliminate divider */}
                    <div>
                      <div
                        className="w-full px-3 py-1.5 text-[9px] rounded-lg"
                        style={{
                          backgroundColor: c.inputBg,
                          color: c.inputPlaceholder,
                          border: `1px solid ${c.borderColor || finalBorderColor}`
                        }}
                      >
                        (555) 123-4567
                      </div>
                    </div>

                    {/* Consent Text + Checkbox - MA-FUNNEL-UI-072: Theme-aware container - exact parity with ConversationFunnel.jsx */}
                    <div 
                      className="mb-2.5 mt-2 px-2.5 py-2.5 rounded-lg" 
                      style={{ 
                        backgroundColor: content.consent_text 
                          ? (c.inputBg || (isLightMode ? '#F2F2F7' : 'rgba(255, 255, 255, 0.08)'))
                          : (isLightMode ? '#FEF2F2' : 'rgba(254, 226, 226, 0.1)'),
                        border: content.consent_text 
                          ? `1px solid ${c.borderColor || finalBorderColor}`
                          : (isLightMode ? '1px solid #FECACA' : '1px solid rgba(254, 202, 202, 0.3)')
                      }}
                    >
                      <div className="flex items-start gap-1.5">
                        {content.require_checkbox !== false && (
                          <div className="flex-shrink-0 mt-px" style={{ width: '10px', height: '10px', border: `1.5px solid ${userColor}`, borderRadius: '2px' }} />
                        )}
                        {content.consent_text ? (
                          <p className="text-[8px] leading-relaxed [&_a]:underline [&_a]:text-[#2563eb]" style={{ color: botBubbleText }} dangerouslySetInnerHTML={{ __html: content.consent_text }} />
                        ) : (
                          <p className="text-[8px] leading-relaxed font-medium" style={{ color: '#f59e0b' }}>
                            Consent language required
                          </p>
                        )}
                      </div>
                    </div>

                    {/* CTA Button - MA-167: Use userColor (accent) for visibility */}
                    <div>
                      <div
                        className="px-3 py-1.5 rounded-lg text-center text-[9px] font-medium"
                        style={{ 
                          background: userColor, 
                          color: userTextColor, 
                          opacity: content.require_checkbox !== false ? 0.6 : 1 
                        }}
                      >
                        {content.button_text || content.submit_button_text || "I Agree & Submit"}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* STANDARD INPUT MODE: Simple input field with submit arrow */
                  <div
                    className="px-3 py-2.5"
                    style={{ background: responseAreaColor, borderTop: '1px solid rgba(0, 0, 0, 0.06)' }}
                  >
                    <div className="flex items-center gap-1.5 w-full">
                      <div
                        className="flex-1 px-3 py-1.5 text-[9px] rounded-lg"
                        style={{
                          backgroundColor: c.inputBg,
                          color: c.inputPlaceholder,
                          border: `1px solid ${c.borderColor || finalBorderColor}`
                        }}
                      >
                        {{ 
                          email: "you@example.com", 
                          phone: "(555) 123-4567", 
                          name: "John Smith", 
                          first_name: "John",
                          last_name: "Smith",
                          zip: "Enter ZIP code" 
                        }[content.input_type] || content.placeholder || "Type your response..."}
                      </div>
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: userColor }}
                      >
                        <ArrowUp className="h-3 w-3" style={{ color: userTextColor }} />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Consent — themed card overlay */}
            {nodeType === "consent" && (
              <div className="px-3 py-2.5" style={{ background: threadColor }}>
                {(() => {
                  const MA_DEBUG_CONSENT = {
                    node_id: node.id,
                    node_type: nodeType,
                    has_consent_text: !!(content.consent_text || funnelConfig?.consent_language),
                    consent_text_source: content.consent_text ? "content" : (funnelConfig?.consent_language ? "funnelConfig" : "none"),
                    has_checkbox_field: content.require_checkbox !== undefined,
                    has_button_text: !!content.button_text,
                    renderer_branch: "consent",
                    timestamp: new Date().toISOString()
                  };
                  console.log('═══════════════════════════════════════════');
                  console.log('MA_DEBUG_CONSENT (PPB):');
                  console.log(JSON.stringify(MA_DEBUG_CONSENT, null, 2));
                  console.log('═══════════════════════════════════════════');
                  return null;
                })()}
                <div
                  className="rounded-lg overflow-hidden"
                  style={{ background: botColor, border: `1px solid ${finalBorderColor}` }}
                >
                  {/* Title */}
                  <div className="px-2.5 pt-2.5 pb-1.5" style={{ borderBottom: `1px solid ${finalBorderColor}` }}>
                    <p className="text-[9px] font-bold" style={{ color: headerText }}>
                      {content.title || "Consent Step"}
                    </p>
                  </div>
                  {/* Consent text with checkbox */}
                  <div className="px-2.5 py-1.5">
                    <div className="flex items-start gap-1.5">
                      {content.require_checkbox !== false && (
                        <div className="flex-shrink-0 mt-px" style={{ width: '10px', height: '10px', border: `1.5px solid ${userColor}`, borderRadius: '2px' }} />
                      )}
                      {(content.consent_text || funnelConfig?.consent_language) ? (
                        <p className="text-[8px] leading-relaxed [&_a]:underline [&_a]:text-[#2563eb]" style={{ color: headerText, opacity: 0.85 }} dangerouslySetInnerHTML={{ __html: content.consent_text || funnelConfig?.consent_language }} />
                      ) : (
                        <p className="text-[8px] leading-relaxed font-medium" style={{ color: '#f59e0b' }}>
                          Consent language required
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Button */}
                  <div className="px-2.5 pb-2.5 pt-1" style={{ background: responseAreaColor }}>
                    {content.require_checkbox !== false && (
                      <p className="text-[7px] text-red-500 text-center mb-1">
                        Please accept the terms and conditions by checking the box above
                      </p>
                    )}
                    <div
                      className="px-3 py-1.5 rounded-md text-center text-[9px] font-medium"
                      style={{ background: userColor, color: userTextColor, opacity: content.require_checkbox !== false ? 0.5 : 1 }}
                    >
                      {content.button_text || "I Agree & Continue"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Booking — MA-FUNNEL-ARCH-077: QuickBookingWidget with compact mode for miniature sizing */}
            {/* Direct render without wrapper containers to fill PPB frame properly */}
            {nodeType === "booking" && (
              <QuickBookingWidget
                preview={true}
                compact={true}
                mockSlots={PPB_MOCK_SLOTS}
                heading={content.heading || "Schedule Your Appointment"}
                subheading={content.subheading}
                showTimezone={true}
                showDuration={true}
                durationMinutes={content.config?.duration_minutes || funnelConfig?.booking_duration || 15}
                tokens={c}
                accentColor={userColor}
                embedded={true}
                contact={{ first_name: 'Demo', last_name: 'User', email: 'demo@example.com', phone: '555-0100' }}
              />
            )}
          </motion.div>
        </div>

        {/* Navigation arrows */}
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-3 z-10">
          <button
            onClick={() => onStepClick?.(Math.max(0, activeStepIndex - 1))}
            disabled={activeStepIndex === 0}
            className="w-6 h-6 rounded-full flex items-center justify-center transition-opacity"
            style={{
              background: `${textMuted}15`,
              opacity: activeStepIndex === 0 ? 0.3 : 1,
              cursor: activeStepIndex === 0 ? "default" : "pointer",
              border: "none",
              color: textMuted,
            }}
            data-testid="preview-nav-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onStepClick?.(Math.min(nodes.length - 1, activeStepIndex + 1))}
            disabled={activeStepIndex === nodes.length - 1}
            className="w-6 h-6 rounded-full flex items-center justify-center transition-opacity"
            style={{
              background: `${textMuted}15`,
              opacity: activeStepIndex === nodes.length - 1 ? 0.3 : 1,
              cursor: activeStepIndex === nodes.length - 1 ? "default" : "pointer",
              border: "none",
              color: textMuted,
            }}
            data-testid="preview-nav-next"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default FunnelLivePreview;
